// Tasarim alani SEKLI ve SIGDIRMA kurallari — urune ozel degil, genel.
//
// Bir alan, mockup fotografindaki baski yuzeyini temsil eder: tisort gogsu
// (dikdortgen), ornament/coin/tabak (elips, kalp), kupa/mum etiketi
// (dikdortgen). Sekil tasarimi KIRPAR; sigdirma tasarimin alana nasil
// yerlesecegini belirler. Yeni sekil = SHAPES'e bir kayit.
//
// SUNUCU KOPYASI: apps/api/src/services/mockup-render.service.ts (AREA_SHAPES)
// — iki taraf ayni sonucu vermeli, birini degistirirsen digerini de.

import { hasWarp, warpTileRGBA, type AreaWarp } from "@erc/shared";

export type AreaShape = "rect" | "ellipse" | "heart";
export type AreaFit = "contain" | "fill";

/** 100x100 birim kutuda kubik bezier zinciri (ilk nokta + [c1, c2, uc] parcalari) */
type BezierPath = { start: [number, number]; segs: Array<[number, number, number, number, number, number]> };

const HEART: BezierPath = {
  start: [50, 98],
  segs: [
    [22, 78, 0, 58, 0, 30],
    [0, 13, 13, 0, 28, 0],
    [39, 0, 46, 6, 50, 15],
    [54, 6, 61, 0, 72, 0],
    [87, 0, 100, 13, 100, 30],
    [100, 58, 78, 78, 50, 98],
  ],
};

export const SHAPE_OPTIONS: Array<{ id: AreaShape; label: string; hint: string }> = [
  { id: "rect", label: "Kare", hint: "Tişört, kupa etiketi, paspas, tablo" },
  { id: "ellipse", label: "Daire", hint: "Yuvarlak ornament, coin, tabak (eğik duruyorsa elips)" },
  { id: "heart", label: "Kalp", hint: "Kalp ornament, kalp tabak" },
];

/** Sekilli alan varsayilan olarak yuzeyi KAPLAR (egik duran daire elips
 *  gorunur; tasarim da ayni oranda basilmali). Dikdortgen orani korur. */
export function effectiveFit(area: { shape?: AreaShape; fit?: AreaFit }): AreaFit {
  return area.fit ?? (area.shape && area.shape !== "rect" ? "fill" : "contain");
}

export function isShaped(area: { shape?: AreaShape }): boolean {
  return !!area.shape && area.shape !== "rect";
}

/** Alanin YEREL kutusunda (0,0)-(w,h) sekil yolunu cizer (beginPath dahil). */
export function traceAreaShape(
  ctx: CanvasRenderingContext2D,
  shape: AreaShape | undefined,
  w: number,
  h: number,
) {
  ctx.beginPath();
  if (!shape || shape === "rect") {
    ctx.rect(0, 0, w, h);
    return;
  }
  if (shape === "ellipse") {
    ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    return;
  }
  const sx = w / 100, sy = h / 100;
  ctx.moveTo(HEART.start[0] * sx, HEART.start[1] * sy);
  for (const [a, b, c, d, e, f] of HEART.segs) {
    ctx.bezierCurveTo(a * sx, b * sy, c * sx, d * sy, e * sx, f * sy);
  }
  ctx.closePath();
}

/** Tasarimin alanin YEREL kutusundaki yeri (dondurme alanin sol-ust kosesinden). */
export function placeInArea(
  area: { w: number; h: number; shape?: AreaShape; fit?: AreaFit },
  natW: number,
  natH: number,
) {
  const { w, h } = area;
  if (effectiveFit(area) === "fill" || !natW || !natH) return { x: 0, y: 0, width: w, height: h };
  const ar = w / h, dr = natW / natH;
  let dw: number, dh: number;
  if (dr > ar) { dw = w; dh = w / dr; } else { dh = h; dw = h * dr; }
  // Dikdortgen dik alanda ust hizali (sunucudaki prepareAreaTile ile ayni); daire/kalp ortada
  const top = (area.shape ?? "rect") === "rect" && h >= w * 0.95;
  return { x: (w - dw) / 2, y: top ? 0 : (h - dh) / 2, width: dw, height: dh };
}

// ---- CARPITMA (silindir/perspektif) — geometri ortak pakette (sunucuyla ayni)
export { hasWarp };
export type { AreaWarp };

/** Tasarimi alanin karosuna yerlestirip (sigdirma + sekil) carpitir; gorsele
 *  basilacak tuvali ve konumunu dondurur. Sunucudaki prepareAreaTile ile ayni. */
export function renderWarpedArea(
  img: CanvasImageSource & { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number },
  area: { x: number; y: number; w: number; h: number; rotation?: number; shape?: AreaShape; fit?: AreaFit; warp?: AreaWarp | null },
): { canvas: HTMLCanvasElement; left: number; top: number } {
  const W = Math.max(1, Math.round(area.w)), H = Math.max(1, Math.round(area.h));
  const tile = document.createElement("canvas");
  tile.width = W; tile.height = H;
  const tctx = tile.getContext("2d", { willReadFrequently: true })!;
  if (isShaped(area)) { traceAreaShape(tctx, area.shape, W, H); tctx.clip(); }
  const natW = (img.naturalWidth || img.width || 0) as number, natH = (img.naturalHeight || img.height || 0) as number;
  const p = placeInArea({ w: W, h: H, shape: area.shape, fit: area.fit }, natW, natH);
  tctx.drawImage(img, p.x, p.y, p.width, p.height);
  const wt = warpTileRGBA(tctx.getImageData(0, 0, W, H).data, W, H, area);
  const out = document.createElement("canvas");
  out.width = wt.width; out.height = wt.height;
  out.getContext("2d")!.putImageData(new ImageData(wt.data as unknown as Uint8ClampedArray<ArrayBuffer>, wt.width, wt.height), 0, 0);
  return { canvas: out, left: wt.left, top: wt.top };
}
